/**
 * Paid media dashboard page — composes filters, tabs, and DashboardTable.
 * Each platform renders this page with a different view prop.
 * Extensible: summary cards, export buttons, and other sections can be added
 * above or below the table without modifying DashboardTable.
 */
import React, { useState, useRef, useMemo, useCallback } from 'react';
import Button from '@amzn/meridian/button';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import { type SortDirection, type OnSort } from '@amzn/meridian/data-table/types/column-sort';
import { useSearchParams } from 'react-router-dom';
import cogTokens from '@amzn/meridian-tokens/base/icon/cog';
import { PageLoader } from 'src/components/common/loaders/page-loader';
import { ColumnPickerModal } from 'src/components/paid-media-dashboard/ColumnPickerModal';
import { DashboardFilterBar, type DashboardFilters, type FilterKey } from 'src/components/paid-media-dashboard/DashboardFilterBar';
import { DashboardTable } from 'src/components/paid-media-dashboard/DashboardTable';
import { ExportButton } from 'src/components/paid-media-dashboard/ExportButton';
import {
  InsightsSection,
  type PaidMediaInsightsConfig,
} from 'src/components/paid-media-insights/InsightsSection';
import { useQueryPaidMediaQuery } from 'src/store/leela-api';
import { toArrayParam, fromArrayParam } from 'src/utils/dashboardUtils';
import { formatDate } from 'src/utils/datePresets';

const PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 500];

type ViewMode = 'summary' | 'all' | 'custom';

const getDefaultStartDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return formatDate(d);
};

const getDefaultEndDate = (): string => formatDate(new Date());

interface PlatformDashboardProps {
  view: string;
  tabs: Array<{ value: string; label: string }>;
  title: string;
  insightsConfig?: PaidMediaInsightsConfig;
  columnWidths?: Record<string, number>;
  columnTooltips?: Record<string, string>;
  formatOverrides?: Record<string, (value: unknown) => string>;
  summaryColumns?: string[];
  alwaysVisibleKeys?: readonly string[];
  filterLabelOverrides?: Partial<Record<FilterKey, string>>;
  currencyColumnKey?: string;
  titleSearchSubViews?: string[];
  breakdownOptions?: Array<{ value: string; label: string }>;
  breakdownExcludedTabs?: string[];
}

const PlatformDashboard: React.FC<PlatformDashboardProps> = ({
  view,
  tabs,
  title,
  insightsConfig,
  columnWidths,
  columnTooltips,
  formatOverrides,
  summaryColumns,
  alwaysVisibleKeys = [],
  filterLabelOverrides,
  currencyColumnKey,
  titleSearchSubViews,
  breakdownOptions,
  breakdownExcludedTabs,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read state from URL params with fallbacks
  const activeTab = searchParams.get('subView') ?? tabs[0].value;
  const breakdownParam = searchParams.get('breakdown') ?? '';
  const isBreakdownExcluded = breakdownExcludedTabs?.includes(activeTab) ?? false;
  const breakdown = !isBreakdownExcluded && breakdownOptions?.some(({ value }) => value === breakdownParam) ? breakdownParam : '';
  const subView = breakdown ? `${activeTab}_${breakdown}` : activeTab;
  const showTitleSearch = titleSearchSubViews?.includes(activeTab) ?? false;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- parseInt returns NaN (not null) for invalid input; ?? doesn't catch NaN
  const page = parseInt(searchParams.get('page') ?? '1', 10) || 1;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const pageSize = parseInt(searchParams.get('pageSize') ?? String(PAGE_SIZE), 10) || PAGE_SIZE;
  const displayCurrency = searchParams.get('displayCurrency') ?? '';
  const sortByParam = searchParams.get('sortBy') ?? undefined;
  // The sorted column could be hidden while converting, so suppress rather than delete the param
  const sortBy = displayCurrency && sortByParam === currencyColumnKey ? undefined : sortByParam;
  const sortOrder = (searchParams.get('sortOrder') ?? 'descending') as SortDirection;

  // Memo filters so DashboardFilterBar's useEffect([filters]) triggers only when URL params actually change.
  const startDateParam = searchParams.get('startDate') ?? getDefaultStartDate();
  const endDateParam = searchParams.get('endDate') ?? getDefaultEndDate();
  const countryParam = searchParams.get('country') ?? '';
  const campaignIdParam = searchParams.get('campaignId') ?? '';
  const objectiveTypeParam = searchParams.get('objectiveType') ?? '';
  const advertiserIdParam = searchParams.get('advertiserId') ?? '';
  const ageParam = searchParams.get('age') ?? '';
  const genderParam = searchParams.get('gender') ?? '';
  const titleParam = searchParams.get('title') ?? '';

  const filters: DashboardFilters = useMemo(() => ({
    startDate: startDateParam,
    endDate: endDateParam,
    title: titleParam,
    country: fromArrayParam(countryParam),
    campaignId: fromArrayParam(campaignIdParam),
    objectiveType: fromArrayParam(objectiveTypeParam),
    advertiserId: fromArrayParam(advertiserIdParam),
    age: fromArrayParam(ageParam),
    gender: fromArrayParam(genderParam),
  }), [startDateParam, endDateParam, titleParam, countryParam, campaignIdParam, objectiveTypeParam, advertiserIdParam, ageParam, genderParam]);
  const effectiveCountryParam = breakdown ? '' : countryParam;
  const effectiveCountry = useMemo(() => fromArrayParam(effectiveCountryParam), [effectiveCountryParam]);

  const queryFilters = useMemo<Record<string, string | string[]>>(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
    ...(showTitleSearch && filters.title.trim() && { title: filters.title.trim() }),
    ...(effectiveCountry.length && { country: effectiveCountry }),
    ...(filters.campaignId.length && { campaignId: filters.campaignId }),
    ...(filters.objectiveType.length && { objectiveType: filters.objectiveType }),
    ...(filters.advertiserId.length && { advertiserId: filters.advertiserId }),
    ...(filters.age.length && { age: filters.age }),
    ...(filters.gender.length && { gender: filters.gender }),
  }), [effectiveCountry, filters, showTitleSearch]);
  const insightsFilters = useMemo<Record<string, string | string[]>>(() => {
    const campaignId = fromArrayParam(campaignIdParam);
    const objectiveType = fromArrayParam(objectiveTypeParam);
    const advertiserId = fromArrayParam(advertiserIdParam);

    return {
      startDate: startDateParam,
      endDate: endDateParam,
      ...(effectiveCountry.length && { country: effectiveCountry }),
      ...(campaignId.length && { campaignId }),
      ...(objectiveType.length && { objectiveType }),
      ...(advertiserId.length && { advertiserId }),
    };
  }, [
    advertiserIdParam,
    campaignIdParam,
    effectiveCountry,
    endDateParam,
    objectiveTypeParam,
    startDateParam,
  ]);
  const querySortOrder = sortBy ? (sortOrder === 'ascending' ? 'asc' : 'desc') : undefined;

  const [viewMode, setViewMode] = useState<ViewMode>('summary');
  const [customSelectedKeys, setCustomSelectedKeys] = useState<string[] | null>(null);
  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);
  const cachedTotalRef = useRef<number>(0);
  const filterOptionsRef = useRef<Record<string, string[]>>({});
  const filterOptionsWindowRef = useRef('');
  const displayOptionValuesRef = useRef<Record<string, string[]>>({});

  // Helper to update URL params — merges with existing, removes empty values
  const updateParams = (updates: Record<string, string>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        value ? next.set(key, value) : next.delete(key);
      }
      return next;
    }, { replace: true });
  };

  const { data, currentData, isLoading, isFetching } = useQueryPaidMediaQuery({
    view,
    subView,
    page,
    pageSize,
    sortBy,
    sortOrder: querySortOrder,
    filters: queryFilters,
    ...(displayCurrency && { displayOptions: { currency: displayCurrency } }),
  });

  if (data?.meta?.pagination?.total != null && page === 1) {
    cachedTotalRef.current = data.meta.pagination.total;
  }
  const filterOptionsWindow = `${startDateParam}|${endDateParam}`;
  if (currentData?.filterOptions && (
    Object.keys(filterOptionsRef.current).length === 0 || filterOptionsWindowRef.current !== filterOptionsWindow
  )) {
    filterOptionsRef.current = currentData.filterOptions;
    filterOptionsWindowRef.current = filterOptionsWindow;
  }
  // Latch each key's first non-empty list (the backend sends `{ currency: [] }` when its options query yields nothing or fails), and read `currentData` so a pending tab change can't re-latch the outgoing sub-view's options.
  for (const [key, values] of Object.entries(currentData?.displayOptionValues ?? {})) {
    if (values.length && !displayOptionValuesRef.current[key]?.length) {
      displayOptionValuesRef.current[key] = values;
    }
  }

  const filterOptions = filterOptionsRef.current;
  const currencyOptions = displayOptionValuesRef.current.currency ?? [];
  // Meridian renders a value with no matching option as blank, so always include the active currency (which also keeps the selector clearable).
  const currencySelectOptions = displayCurrency && !currencyOptions.includes(displayCurrency)
    ? [displayCurrency, ...currencyOptions]
    : currencyOptions;

  const pageCount = (data?.meta?.pagination?.pageCount ?? Math.ceil(cachedTotalRef.current / pageSize)) || 1;
  const allColumns = useMemo(() => data?.columns ?? [], [data?.columns]);

  // A converted row's local-currency column would be misleading, so hide it from the table and the picker alike.
  const availableColumns = useMemo(() => (
    displayCurrency && currencyColumnKey
      ? allColumns.filter(c => c.key !== currencyColumnKey)
      : allColumns
  ), [allColumns, displayCurrency, currencyColumnKey]);

  // Derive visible columns based on viewMode
  const columns = useMemo(() => {
    switch (viewMode) {
      case 'summary':
        return summaryColumns
          ? availableColumns.filter(c => summaryColumns.includes(c.key))
          : availableColumns;
      case 'all':
        return availableColumns;
      case 'custom': {
        const visibleSet = new Set([...(customSelectedKeys ?? []), ...alwaysVisibleKeys]);
        return availableColumns.filter(c => visibleSet.has(c.key));
      }
    }
  }, [viewMode, availableColumns, summaryColumns, customSelectedKeys, alwaysVisibleKeys]);

  const rows = data?.data ?? [];
  const totalResults = cachedTotalRef.current > 0 ? cachedTotalRef.current : (data?.meta?.pagination?.total ?? 0);
  const isLoadingPage = isFetching && !isLoading;

  const onSort: OnSort = ({ sortColumn: col, sortDirection: dir }) => {
    if (dir !== 'none') {
      updateParams({ sortBy: col, sortOrder: dir, page: '1' });
    }
  };

  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('subView', tab);
      next.set('page', '1');
      next.delete('breakdown');
      next.delete('sortBy');
      next.delete('sortOrder');
      next.delete('country');
      next.delete('campaignId');
      next.delete('objectiveType');
      next.delete('advertiserId');
      next.delete('age');
      next.delete('gender');
      next.delete('title');
      return next;
    }, { replace: true });
    filterOptionsRef.current = {};
    filterOptionsWindowRef.current = '';
    displayOptionValuesRef.current = {};
  };

  const handleBreakdownChange = (value: string): void => {
    updateParams(value
      ? { breakdown: value, country: '', sortBy: '', sortOrder: '', page: '1' }
      : { breakdown: '', age: '', gender: '', sortBy: '', sortOrder: '', page: '1' });
    filterOptionsRef.current = {};
    filterOptionsWindowRef.current = '';
    displayOptionValuesRef.current = {};
  };

  const handleDisplayCurrencyChange = (value: string): void => {
    updateParams({ displayCurrency: value, page: '1' });
  };

  const handleApply = (applied: DashboardFilters): void => {
    updateParams({
      startDate: applied.startDate,
      endDate: applied.endDate,
      country: toArrayParam(applied.country),
      campaignId: toArrayParam(applied.campaignId),
      objectiveType: toArrayParam(applied.objectiveType),
      advertiserId: toArrayParam(applied.advertiserId),
      age: toArrayParam(applied.age),
      gender: toArrayParam(applied.gender),
      title: showTitleSearch ? applied.title.trim() : '',
      page: '1',
    });
  };

  const handleViewModeChange = useCallback((val: string) => {
    if (val === 'custom') {
      setViewMode('custom');
      if (customSelectedKeys === null) {
        setIsColumnPickerOpen(true);
      }
    } else {
      setViewMode(val as ViewMode);
    }
  }, [customSelectedKeys]);

  const handleColumnPickerConfirm = useCallback((selectedKeys: readonly string[]) => {
    setCustomSelectedKeys([...selectedKeys]);
    setViewMode('custom');
    setIsColumnPickerOpen(false);
  }, []);

  const handleColumnPickerCancel = useCallback(() => {
    setIsColumnPickerOpen(false);
    if (customSelectedKeys === null) {
      setViewMode('summary');
    }
  }, [customSelectedKeys]);

  const handleGearClick = useCallback(() => {
    setIsColumnPickerOpen(true);
  }, []);

  return (
    <Column alignmentHorizontal="stretch" data-testid="platform-dashboard" heights="fill" spacing="400" spacingInset="none 400 500 none" width="100%">
      <Heading data-testid="dashboard-title" level={1} type="h500">
        {title}
      </Heading>

      <Row alignmentVertical="bottom" spacing="300" wrap="down">
        <DashboardFilterBar
          filterLabelOverrides={filterLabelOverrides}
          filterOptions={filterOptions}
          filters={filters}
          showTitleSearch={showTitleSearch}
          onApply={handleApply}
        />
        {currencySelectOptions.length > 0 && (
          <Select
            data-testid="dashboard-display-currency"
            label="Display Currency"
            size="small"
            value={displayCurrency}
            width={180}
            onChange={handleDisplayCurrencyChange}
          >
            <SelectOption label="Local (no conversion)" value="" />
            {currencySelectOptions.map((c) => (
              <SelectOption key={c} label={c} value={c} />
            ))}
          </Select>
        )}
      </Row>

      {insightsConfig && (
        <InsightsSection
          config={insightsConfig}
          displayCurrency={displayCurrency}
          filters={insightsFilters}
          view={view}
        />
      )}

      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <TabGroup data-testid="dashboard-tabs" value={activeTab} onChange={handleTabChange}>
          {tabs.map((tab) => (
            <Tab data-testid={`dashboard-tab-${tab.value}`} key={tab.value} value={tab.value}>{tab.label}</Tab>
          ))}
        </TabGroup>
        {breakdownOptions && !isBreakdownExcluded && (
          <ButtonGroup
            data-testid="dashboard-breakdown"
            legend="Breakdown"
            size="small"
            type="outline"
            value={breakdown}
            onChange={handleBreakdownChange}
          >
            <ButtonOption value="">No Breakdown</ButtonOption>
            {breakdownOptions.map((option) => (
              <ButtonOption key={option.value} value={option.value}>{option.label}</ButtonOption>
            ))}
          </ButtonGroup>
        )}
      </Row>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}>
        {summaryColumns && (
          <>
            <TabGroup data-testid="dashboard-column-toggle" value={viewMode} onChange={handleViewModeChange}>
              <Tab data-testid="dashboard-toggle-summary" value="summary">Summary</Tab>
              <Tab data-testid="dashboard-toggle-all" value="all">All Metrics</Tab>
              <Tab data-testid="dashboard-toggle-custom" value="custom">Custom</Tab>
            </TabGroup>
            <Button
              aria-label="customize columns"
              data-testid="column-picker-gear-btn"
              type="icon"
              onClick={handleGearClick}
            >
              <Icon tokens={cogTokens} />
            </Button>
          </>
        )}
        <ExportButton
          filters={queryFilters}
          sortBy={sortBy}
          sortOrder={querySortOrder}
          subView={subView}
          view={view}
        />
      </div>

      {isLoading
        ? (
        <Column alignmentHorizontal="center" alignmentVertical="center" height="300px">
          <PageLoader message="Loading..." />
        </Column>
          )
        : (
        <DashboardTable
          columnTooltips={columnTooltips}
          columnWidths={columnWidths}
          columns={columns}
          formatOverrides={formatOverrides}
          isLoadingPage={isLoadingPage}
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          rows={rows}
          sortBy={sortBy}
          sortOrder={sortOrder}
          totalResults={totalResults}
          onPageChange={(p: number) => { updateParams({ page: String(p) }); }}
          onPageSizeChange={(val: number) => { updateParams({ pageSize: String(val), page: '1' }); }}
          onSort={onSort}
        />
          )}

      {isColumnPickerOpen && (
        <ColumnPickerModal
          alwaysVisibleKeys={alwaysVisibleKeys}
          columns={availableColumns}
          selectedKeys={customSelectedKeys}
          onCancel={handleColumnPickerCancel}
          onConfirm={handleColumnPickerConfirm}
        />
      )}
    </Column>
  );
};

export default PlatformDashboard;
